import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { usePostHog } from "posthog-js/react";
import Button from "~/components-shared/Button";
import companySelectors from "~/store/company/selectors";
import { StatusStateEnum } from "~/types/enums";
import Modal from "~/components-shared/Modal";
import toastsActionCreators from "~/store/toasts/actions";
import { Alert } from "~/components-shared";
import CompanyForm from "~/forms/CompanyForm";
import companyActionCreators from "~/store/company/actions";
import companySchema from "~/forms/CompanyForm/companySchema";
import { helpers } from "~/lib/helpers";
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const CompanyUpdateModal = () => {
  const {
    t
  } = useTranslation();
  const posthog = usePostHog();
  const company = useSelector(companySelectors.getCompany);
  const status = useSelector(companySelectors.getStatus);
  const error = useSelector(companySelectors.getError);
  const dispatch = useDispatch();
  const defaultValues = {
    name: company?.name || "",
    description: company?.description || "",
    url: company?.url || "",
    employeeCount: company?.employeeCount || 0,
    emailDomains: company?.emailDomains || "",
    logo: company?.logo || ""
  };
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    setError
  } = useForm({
    resolver: yupResolver(companySchema),
    defaultValues
  });
  useEffect(() => {
    if (company) {
      reset(defaultValues);
    }
  }, [company]);
  const onSubmit = handleSubmit(data => {
    dispatch(companyActionCreators.updateStarted(data, _ref => {
      let {
        data: newCompany
      } = _ref;
      if (newCompany) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("CompanyUpdate.companyUpdated"),
          timeout: 10000
        }));
      }
    }));
    posthog.capture(`Company Update – Save`, {
      ...helpers.objectDiff(defaultValues, data)
    });
  });
  const handleCancel = () => {
    dispatch(companyActionCreators.actionChanged(undefined));
    posthog.capture(`Company Update – Cancel`);
  };
  return /*#__PURE__*/React.createElement(Modal.Body, {
    onClose: handleCancel,
    maxWidth: "640px",
    title: t("CompanyUpdate.updateCompany")
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(CompanyForm, {
    control: control,
    watch: watch,
    setValue: setValue,
    setError: setError,
    company: company
  })), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: handleCancel,
    rounded: true,
    display: "inline-block"
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    display: "inline-block",
    maxWidth: "230px",
    type: "submit",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.save")))));
};
export default CompanyUpdateModal;